import { useCallback, useState } from 'react';
import { FlatList, Pressable, RefreshControl, Text } from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { pendingSubmissions } from '../api/submissions';
import { Submission } from '../types';
import { Card, EmptyState, ErrorText, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';

export default function OwnerPendingReviewScreen() {
  const { token } = useAuth();
  const router = useRouter();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setSubmissions(await pendingSubmissions(token));
      setLoadError(null);
    } catch (e) {
      // Previously unhandled — a failed fetch left submissions stale/empty
      // and rendered the same "no submissions pending" EmptyState as a
      // genuinely empty list, with no way for the owner to tell the two apart.
      setLoadError(e instanceof ApiError ? e.message : 'Không tải được danh sách bài chờ duyệt, vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  return (
    <ScreenContainer safeTop safeBottom>
      {loadError ? <ErrorText>{loadError}</ErrorText> : null}
      <FlatList
        data={submissions}
        keyExtractor={(item) => item.id}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={load} />}
        ListEmptyComponent={!loading && !loadError ? <EmptyState text="Không có bài nào chờ duyệt" /> : null}
        renderItem={({ item }) => (
          <Pressable onPress={() => router.push(`/submission/${item.id}`)}>
            <Card>
              <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: colors.text }}>
                {item.task?.title ?? 'Nhiệm vụ'}
              </Text>
              <Text style={{ color: colors.textMuted, marginTop: 2 }}>
                Người review: {item.reviewer?.name ?? item.reviewerId}
              </Text>
              <Text style={{ color: colors.textMuted, marginTop: 2, fontSize: 12 }}>
                {item.photos.length} ảnh · nộp lúc{' '}
                {item.submittedAt ? new Date(item.submittedAt).toLocaleString('vi-VN') : ''}
              </Text>
            </Card>
          </Pressable>
        )}
      />
    </ScreenContainer>
  );
}
